import React, { useRef, useState, useEffect } from "react";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import axiosInstance from "../axiosConfig";
import Swal from "sweetalert2";
import loaderGif from "../assets/Img/loading1.gif";
import "../assets/CSS/Dashboard.css";

function getDefaultMonthYear() {
  const today = new Date();
  let month = today.getMonth(); // 0 = Jan, 11 = Dec
  let year = today.getFullYear();

  if (month === 0) {
    // January → previous year December
    month = 12;
    year = year - 1;
  } else {
    month = month; // previous month (1–12)
  }

  return { month, year };
}

export default function SalaryFinalization() {

  /* ================= BASIC ================= */
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);

  const user = JSON.parse(localStorage.getItem("user")) || {};
  const user_type = user.Auth;

  /* ================= UPLOAD ================= */
  const [selectedFile, setSelectedFile] = useState(null);
  const defaultDate = getDefaultMonthYear();

  const [month, setMonth] = useState(defaultDate.month);
  const [year, setYear] = useState(defaultDate.year);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  /* ================= RESULT ================= */
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  /* ================= OPTIONS ================= */
  const months = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: new Date(2000, i, 1).toLocaleString("en-US", { month: "long" })
  }));

  const [exporting, setExporting] = useState(false);

  const currentYear = new Date().getFullYear();

  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  useEffect(() => {
    calculateSalary();
  }, []);

  /* ================= UPLOAD ================= */
  const handleExcelUpload = async () => {
    if (!selectedFile || !month || !year) {
      Swal.fire("Required", "Please select year, month and Excel file", "warning");
      return;
    }

    const fd = new FormData();
    fd.append("file", selectedFile);
    fd.append("month", month); // INT (1–12)
    fd.append("year", year);   // INT

    try {
      setUploading(true);
      setData([]);

      const res = await axiosInstance.post(
        "/api/salary/upload",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      Swal.fire("Success", res.data.message, "success");

      setSelectedFile(null);
      fileInputRef.current.value = "";
      calculateSalary();
    } catch (err) {
      Swal.fire(
        "Error",
        err?.response?.data?.error || "Excel upload failed",
        "error"
      );
    } finally {
      setUploading(false);
    }
  };

  /* ================= download excel sheet ================= */
  const exportSalary = async () => {
    if (!month || !year) {
      Swal.fire("Required", "Please select month and year", "warning");
      return;
    }

    try {
      setExporting(true);

      const res = await axiosInstance.get(
        `/api/salary/export?month=${month}&year=${year}`,
        { responseType: "blob" }
      );

      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `Salary_${month}_${year}.xlsx`;
      a.click();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (err.response?.data instanceof Blob) {
        const text = await err.response.data.text();
        try {
          const json = JSON.parse(text);
          Swal.fire("Info", json.message || "No data available", "info");
        } catch {
          Swal.fire("Error", "Unable to export salary", "error");
        }
      } else {
        Swal.fire("Error", err?.response?.data?.message || "Export error", "error");
      }
    } finally {
      setExporting(false);
    }
  };

  /* ================= CALCULATE ================= */
  const calculateSalary = async () => {
    if (!month || !year) {
      Swal.fire("Required", "Please select month and year", "warning");
      return;
    }

    try {
      setLoading(true);
      const res = await axiosInstance.get(
        `/api/salary/calculate?month=${month}&year=${year}`
      );
      setData(res.data || []);
    } catch (err) {
      Swal.fire("Error", "Salary calculation failed", "error");
    } finally {
      setLoading(false);
    }
  };

  /* ================= UI ================= */
  return (
    <div className="d-flex dashboard">

      {/* ===== LEFT MENU ===== */}
      <div className={isSidebarVisible ? "sidebar-container" : "sidebar-hidden"}>
        <Sidebar />
      </div>

      {/* ===== RIGHT ===== */}
      <div className="flex-grow-1">
        <Header toggleSidebar={toggleSidebar} isSidebarVisible={isSidebarVisible} />

        <main className="main-container p-4">

          <h3 className="mb-4">Salary Finalization</h3>

          {/* ================= CONTROLS ================= */}
          {(user_type === 'SuperAdmin' || user_type === 'Admin' || ["4001","1021"].includes(user?.EmployeeID)) && (
            <div className="card p-3 mb-4">
              <div className="row g-3 align-items-end">

                <div className="col-md-2">
                  <label className="form-label fw-bold">Year</label>
                  <select
                    className="form-select"
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                  >
                    <option value="">Select Year</option>
                    {years.map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>

                <div className="col-md-2">
                  <label className="form-label fw-bold">Month</label>
                  <select
                    className="form-select"
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                  >
                    <option value="">Select Month</option>
                    {months.map(m => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label fw-bold">Upload Salary Excel</label>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    className="form-control"
                    ref={fileInputRef}
                    onChange={(e) => setSelectedFile(e.target.files[0])}
                  />
                </div>

                <div className="col-md-4">
                  <button
                    className="btn btn-success me-2"
                    disabled={uploading}
                    onClick={handleExcelUpload}
                  >
                    {uploading ? "Uploading..." : "Upload Excel"}
                  </button>

                  <button
                    className="btn btn-primary me-2"
                    onClick={calculateSalary}
                  >
                    Calculate
                  </button>

                  <button
                    className="btn btn-secondary"
                    disabled={exporting}
                    onClick={exportSalary}
                  >
                    {exporting ? "Exporting..." : "Bank Mandate"}
                  </button>
                </div>

              </div>
            </div>
          )}

          {/* ================= LOADER ================= */}
          {(loading || uploading) && (
            <div
              className="card d-flex justify-content-center align-items-center"
              style={{ minHeight: 300 }}
            >
              <img src={loaderGif} alt="loading" width={120} />
              <div className="mt-2 text-muted fw-semibold">
                {uploading ? "Processing Excel..." : "Loading Salary Data..."}
              </div>
            </div>
          )}

          {/* ===== EMPTY ===== */}
          {!loading && !uploading && data.length === 0 && (
            <div
              className="card d-flex justify-content-center align-items-center"
              style={{ minHeight: 250 }}
            >
              <div className="text-muted fw-semibold fs-5">
                Data not found
              </div>
              <div className="text-muted small">
                No salary records for selected Month / Year
              </div>
            </div>
          )}

          {/* ================= RESULT ================= */}
          {!loading && data.length > 0 && (
            <div className="card shadow-sm">
              <div className="card-body table-responsive">
                <table className="table table-bordered table-sm">
                  <thead className="table-light">
                    <tr>
                      <th>Staff Name</th>
                      <th>Work Days</th>
                      <th>Base Salary</th>
                      <th>PT</th>
                      <th>Present Count</th>
                      <th>Actual Present Count</th>
                      <th>Holidays</th>
                      <th>Extra Days</th>
                      <th>Valid OT in Minutes</th>
                      <th>Late Count</th>
                      <th>Late Salary Deduction</th>
                      <th>Valid OT</th>
                      <th>Total Pay in Days</th>
                      <th>Total Pay Minutes</th>
                      {/*<th>Actual Per Minutes Salary Without OT</th>*/}
                      {/*<th>Calculated Salary</th>*/}
                      <th>Performance %</th>
                      <th>Bonus</th>
                      <th>Deduction</th>
                      {/*<th>Final Salary</th>*/}
                      <th>Advance</th>
                      <th>Payable</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((r, i) => {

                      const totalSalary   = Number(r.TotalPay || 0);
                      const bonus         = Number(r.BonusAmount || 0);
                      const deduction     = Number(r.DeductionAmount || 0);
                      const advance       = Number(r.Advance || 0);

                      const finalSalary   = totalSalary + bonus - deduction;

                      const baseSalary     = Number(r.BaseSalary || 0);
                      const validOT        = Number(r.ValidOT || 0);

                      const pt             = Number(r.PT || 0);
                      const lateDeduction  = Number(r.LateSalaryDeduction || 0);

                      // const payableSalary =
                      //   (baseSalary + validOT + bonus)
                      //   - (pt + lateDeduction + deduction + advance);

                      const payableSalary =
                        (r. TotalPayMinutes + bonus)
                        - (deduction + advance);


                      return (
                        <tr key={i}>
                          <td>{r.EmployeeName}</td>
                          <td>{r.WorkDays}</td>

                          <td>{Number(r.BaseSalary).toFixed(2)}</td>
                          <td>{r.PT ?? 0.00}</td>

                          <td>{r.ActualPresentCount ?? 0}</td>
                          <td>{r.PresentCount ?? 0}</td>

                          <td>{r.Holidays ?? 0 }</td>
                          <td>{r.ExtraDays ?? 0 }</td>
                          <td>{r.ValidOTMinutes ?? 0 }</td>

                          <td>{r.LateCount ?? 0 }</td>
                          <td>{r.LateSalaryDeduction ?? 0.00 }</td>
                          <td>{r.ValidOT ?? 0.00 }</td>
                          <td>{r.TotalPayInDays ?? 0.00 }</td>
                          <td>{r.TotalPayMinutes ?? 0.00 }</td>

                          <td>{r.PerformancePercentage}</td>
                          <td>{bonus.toFixed(2)}</td>
                          <td>{deduction.toFixed(2)}</td>
                          {/*<td className="fw-bold text-success">
                            {finalSalary.toFixed(2)}
                          </td>*/}
                          <td>{advance.toFixed(2)}</td>
                          {/* Payable Salary */}
                          <td className="fw-bold text-primary">
                            {payableSalary.toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}

                  </tbody>

                </table>
              </div>
            </div>
          )}

        </main>
      </div>
    </div>
  );
}
